import {
  useEffect,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { pushDataLayer } from '@hungryroot/google-analytics';
import { workmagic } from '@hungryroot/o11y';
import {
  AddressForm,
  type Address,
  type AddressDetails,
  type ErrorFields,
} from '@hungryroot/pages-checkout';
import { type Customer } from '@hungryroot/rest';
import { funnelAnalytics } from '@hungryroot/segment';
import {
  type DateInfo,
  type PossibleDays,
} from '../../../../api/get_possible_days/get/get_possible_days';
import { FirstDeliveryDate } from '../../Components/FirstDeliveryDate/FirstDeliveryDate';

interface Props {
  testid?: string;
  customer: Customer;
  orderId: number;
  displayAddress?: Address;
  displayAddressDetails?: AddressDetails;
  addressState?: Address;
  addressDetailsState?: AddressDetails;
  errorMessage?: ErrorFields;
  setAddressState: Dispatch<SetStateAction<Address | undefined>>;
  setAddressDetailsState: Dispatch<SetStateAction<AddressDetails | undefined>>;
  setErrorMessage: Dispatch<SetStateAction<ErrorFields | undefined>>;
  setSelectedDate: Dispatch<SetStateAction<DateInfo | undefined>>;
  selectedDate: DateInfo | undefined;
  deliveryDates: PossibleDays | undefined;
  deliveryDateError: string;
  deliveryDateLoading: boolean;
  containerRef: RefObject<HTMLDivElement>;
}

export function Shipping(props: Props) {
  const {
    testid,
    customer,
    orderId,
    addressState,
    setAddressState,
    addressDetailsState,
    setAddressDetailsState,
    errorMessage,
    setErrorMessage,
    displayAddress,
    displayAddressDetails,
    setSelectedDate,
    selectedDate,
    deliveryDates,
    deliveryDateError,
    deliveryDateLoading,
    containerRef,
  } = props;

  const { zipcode } = displayAddress || {};
  const analytics = funnelAnalytics();

  useEffect(() => {
    if (!(customer.id && orderId)) {
      return;
    }

    pushDataLayer({
      event: `hr.checkout.start`,
      customer: {
        email: customer.email,
        id: customer.id,
      },
    }); // GTM has a trigger on this event
    analytics.checkoutStarted(customer.id);
    workmagic.checkoutStarted(orderId);
  }, [customer.id, orderId]);

  return (
    <div data-testid={testid}>
      <AddressForm
        testid={testid}
        customer={customer}
        addressState={addressState}
        setAddressState={setAddressState}
        addressDetailsState={addressDetailsState}
        setAddressDetailsState={setAddressDetailsState}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        displayAddress={displayAddress}
        displayAddressDetails={displayAddressDetails}
        containerRef={containerRef}
      />
      <FirstDeliveryDate
        testid={testid}
        error={deliveryDateError}
        isLoading={deliveryDateLoading}
        orderId={orderId}
        setSelectedDate={setSelectedDate}
        selectedDate={selectedDate}
        deliveryDates={deliveryDates}
        zipcode={zipcode}
      />
    </div>
  );
}
